import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { COA_TYPE_ORDER, COA_TYPE_LABELS } from './accountingUiConstants';

const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

const emptyLine = () => ({ account_id: '', debit: '', credit: '', memo: '' });

export default function AccountingJournals() {
  const [coa, setCoa] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jDate, setJDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [jMemo, setJMemo] = useState('');
  const [jLines, setJLines] = useState([emptyLine(), emptyLine()]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, j] = await Promise.all([
        api.get('/tenant/accounting/coa'),
        api.get('/tenant/accounting/journals', { params: { limit: 50 } }),
      ]);
      setCoa(a.data.accounts || []);
      setJournals(j.data.journals || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Gagal memuat jurnal'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCoa = useMemo(
    () => coa.filter((x) => x.is_active !== false).sort((a, b) => String(a.code).localeCompare(String(b.code), 'id')),
    [coa],
  );

  const coaByType = useMemo(() => {
    const m = Object.fromEntries(COA_TYPE_ORDER.map((t) => [t, []]));
    activeCoa.forEach((a) => {
      const t = a.type && m[a.type] !== undefined ? a.type : 'expense';
      if (m[t]) m[t].push(a);
    });
    return m;
  }, [activeCoa]);

  const lineTotals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    jLines.forEach((l) => {
      debit += parseInt(String(l.debit).replace(/\D/g, ''), 10) || 0;
      credit += parseInt(String(l.credit).replace(/\D/g, ''), 10) || 0;
    });
    return { debit, credit, diff: debit - credit };
  }, [jLines]);

  const updateLine = (i, patch) => {
    setJLines((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], ...patch };
      return n;
    });
  };

  const removeLine = (i) => {
    if (jLines.length <= 2) return;
    setJLines((prev) => prev.filter((_, idx) => idx !== i));
  };

  const resetForm = () => {
    setJDate(new Date().toISOString().slice(0, 10));
    setJMemo('');
    setJLines([emptyLine(), emptyLine()]);
  };

  const submitJournal = async (e) => {
    e.preventDefault();
    const lines = jLines
      .map((l) => ({
        account_id: l.account_id,
        debit: parseInt(String(l.debit).replace(/\D/g, ''), 10) || 0,
        credit: parseInt(String(l.credit).replace(/\D/g, ''), 10) || 0,
        memo: l.memo || '',
      }))
      .filter((l) => l.account_id);

    if (lines.length < 2) {
      toast.error('Minimal dua baris dengan akun terisi (jurnal double-entry).');
      return;
    }
    if (lineTotals.debit !== lineTotals.credit) {
      toast.error(`Jurnal tidak seimbang: debit Rp ${fmt(lineTotals.debit)} vs kredit Rp ${fmt(lineTotals.credit)}.`);
      return;
    }
    if (lineTotals.debit <= 0) {
      toast.error('Total debit/kredit harus lebih dari nol.');
      return;
    }

    try {
      await api.post('/tenant/accounting/journals', { entry_date: jDate, memo: jMemo, lines });
      toast.success('Jurnal tersimpan');
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Jurnal gagal');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  const balanced = lineTotals.debit === lineTotals.credit && lineTotals.debit > 0;

  return (
    <div className="space-y-6" data-testid="accounting-journals-page">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} type="button">
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Muat ulang
        </Button>
      </div>

      <Card className="overflow-hidden border-slate-200/90 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="text-lg text-slate-900">Buat jurnal manual</CardTitle>
              <CardDescription className="mt-1.5 max-w-2xl leading-relaxed">
                Isi tanggal dan keterangan umum, lalu tambah baris: setiap baris memilih akun dan mengisi <strong>debit</strong> atau{' '}
                <strong>kredit</strong> (bukan keduanya). Total debit harus sama dengan total kredit.
              </CardDescription>
            </div>
            <Badge variant="outline" className="mt-2 w-fit shrink-0 border-slate-200 bg-white text-slate-600 sm:mt-0">
              Double-entry
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <form onSubmit={submitJournal} className="space-y-0">
            <div className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-12 lg:gap-6">
              <div className="space-y-2 lg:col-span-3">
                <Label htmlFor="jv-date" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tanggal transaksi
                </Label>
                <Input
                  id="jv-date"
                  type="date"
                  className="h-10 bg-white"
                  value={jDate}
                  onChange={(e) => setJDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2 sm:col-span-2 lg:col-span-9">
                <Label htmlFor="jv-memo" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Keterangan jurnal
                </Label>
                <Input
                  id="jv-memo"
                  className="h-10 bg-white"
                  value={jMemo}
                  onChange={(e) => setJMemo(e.target.value)}
                  placeholder="Contoh: Pembayaran listrik Maret 2025"
                />
              </div>
            </div>

            <Separator />

            <div className="px-6 py-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Detail baris jurnal</p>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 bg-slate-50/90 hover:bg-slate-50/90">
                      <TableHead className="w-10 pl-3 text-center text-slate-600">#</TableHead>
                      <TableHead className="min-w-[220px] text-slate-700">Akun</TableHead>
                      <TableHead className="w-[130px] text-right text-slate-700">Debit (Rp)</TableHead>
                      <TableHead className="w-[130px] text-right text-slate-700">Kredit (Rp)</TableHead>
                      <TableHead className="min-w-[160px] text-slate-700">Keterangan baris</TableHead>
                      <TableHead className="w-12 pr-2 text-center text-slate-600" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jLines.map((l, i) => (
                      <TableRow key={i} className="border-slate-100">
                        <TableCell className="pl-3 text-center text-xs font-medium text-slate-400">{i + 1}</TableCell>
                        <TableCell className="py-2">
                          <Select
                            value={l.account_id || undefined}
                            onValueChange={(v) => updateLine(i, { account_id: v })}
                          >
                            <SelectTrigger className="h-10 w-full border-slate-200 bg-white text-left">
                              <SelectValue placeholder="Pilih akun…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {COA_TYPE_ORDER.map((type) => {
                                const list = coaByType[type] || [];
                                if (!list.length) return null;
                                return (
                                  <SelectGroup key={type}>
                                    <SelectLabel className="text-slate-500">{COA_TYPE_LABELS[type]}</SelectLabel>
                                    {list.map((a) => (
                                      <SelectItem key={a.id} value={a.id} className="font-mono text-sm">
                                        {a.code} — {a.name}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-2">
                          <Input
                            inputMode="numeric"
                            className="h-10 border-slate-200 text-right tabular-nums"
                            placeholder="0"
                            value={l.debit}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateLine(i, { debit: v, ...(v ? { credit: '' } : {}) });
                            }}
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <Input
                            inputMode="numeric"
                            className="h-10 border-slate-200 text-right tabular-nums"
                            placeholder="0"
                            value={l.credit}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateLine(i, { credit: v, ...(v ? { debit: '' } : {}) });
                            }}
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <Input
                            className="h-10 border-slate-200"
                            placeholder="Opsional"
                            value={l.memo}
                            onChange={(e) => updateLine(i, { memo: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="py-2 pr-2 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-slate-400 hover:text-red-600"
                            disabled={jLines.length <= 2}
                            onClick={() => removeLine(i)}
                            aria-label="Hapus baris"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button type="button" variant="outline" size="sm" className="w-fit border-dashed" onClick={() => setJLines([...jLines, emptyLine()])}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Tambah baris
                </Button>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-500">
                    Total debit: <strong className="tabular-nums text-slate-900">Rp {fmt(lineTotals.debit)}</strong>
                  </span>
                  <span className="hidden text-slate-300 sm:inline">|</span>
                  <span className="text-slate-500">
                    Total kredit: <strong className="tabular-nums text-slate-900">Rp {fmt(lineTotals.credit)}</strong>
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'font-normal',
                      balanced
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : lineTotals.debit > 0 || lineTotals.credit > 0
                          ? 'border-amber-200 bg-amber-50 text-amber-900'
                          : 'border-slate-200 bg-slate-50 text-slate-600',
                    )}
                  >
                    {balanced
                      ? 'Seimbang'
                      : lineTotals.debit === 0 && lineTotals.credit === 0
                        ? 'Belum ada nominal'
                        : `Selisih Rp ${fmt(Math.abs(lineTotals.diff))}`}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
              <Button type="button" variant="ghost" size="sm" className="text-slate-600" onClick={resetForm}>
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Kosongkan form
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90" disabled={!balanced}>
                  Simpan & posting jurnal
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-slate-200/90 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-4">
          <CardTitle className="text-lg text-slate-900">Jurnal terbaru</CardTitle>
          <CardDescription>50 entri terakhir di tenant Anda.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead className="pl-4 text-slate-700">Tanggal</TableHead>
                <TableHead className="text-slate-700">No. jurnal</TableHead>
                <TableHead className="text-slate-700">Sumber</TableHead>
                <TableHead className="text-slate-700">Memo</TableHead>
                <TableHead className="pr-4 text-right text-slate-700">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {journals.map((j) => {
                const tot = (j.lines || []).reduce((s, l) => s + (l.debit || 0), 0);
                return (
                  <TableRow key={j.id} className="border-slate-100">
                    <TableCell className="pl-4 tabular-nums text-slate-700">{j.entry_date}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-800">{j.entry_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-200 bg-white font-normal capitalize text-slate-600">
                        {j.source_type || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[240px] truncate text-slate-700">{j.memo || '—'}</TableCell>
                    <TableCell className="pr-4 text-right font-medium tabular-nums text-slate-900">Rp {fmt(tot)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!journals.length && (
            <p className="px-6 py-10 text-center text-sm text-slate-400">Belum ada jurnal. Buat entri pertama di atas.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
